/**
 * AetherLite Plans CRUD + share links.
 *
 * IMPORTANTE — contrato con backend:
 *   El backend (`aetherlite_plans.py`) modela el plan con columnas separadas:
 *     - `name`        (string, antes lo llamábamos `title`)
 *     - `template`    (string)
 *     - `client_id`   (FK opcional a `al_client`)
 *     - `client_name` (denormalización; se sincroniza desde `al_client.full_name`
 *                      cuando hay client_id)
 *     - `sections[]`  (JSONB list — el cuerpo del editor; antes lo enviábamos como
 *                      `data: PlanDoc` envuelto)
 *
 *   La versión anterior de este archivo usaba `title` + `data: any`, lo que rompía
 *   el shape del backend desde B1. Ya está corregido — los callers deben usar
 *   `name` y `sections`.
 *
 * Endpoints (todos requieren auth excepto getPublicPlan):
 *   GET    /aetherlite/plans
 *   GET    /aetherlite/plans/:id
 *   POST   /aetherlite/plans
 *   PUT    /aetherlite/plans/:id
 *   DELETE /aetherlite/plans/:id
 *   POST   /aetherlite/plans/:id/share
 *   DELETE /aetherlite/plans/:id/share/:shortCode
 *   GET    /aetherlite/p/:shortCode (público, sin auth)
 */
import { apiFetch } from "./client";
import type { PlanBrand } from "../types/plan";

/** Resumen lite — `GET /plans` no devuelve `sections` para evitar payloads grandes. */
export interface PlanListItem {
  id: number;
  name: string;
  template: string;
  client_id: number | null;
  client_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Detalle completo de un plan, incluyendo `sections` y los share codes activos. */
export interface Plan {
  id: number;
  user_id: number;
  name: string;
  template: string;
  client_id: number | null;
  client_name: string | null;
  /** Forma libre — el editor maneja `PlanSection[]` (ver types/plan.ts). */
  sections: any[];
  /** Estilo del plan persistido (Feature 3): paleta/tipografía/densidad/tema. */
  brand: PlanBrand | null;
  share_codes: string[];
  created_at: string;
  updated_at: string;
}

export interface PlanShare {
  /** Short URL-safe code, ej "Aa1Bb2". */
  short_code: string;
  /** Null si no expira. */
  expires_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
}

/**
 * PublicPlanView — shape exacto del response público (`aetherlite_plans.public_plan`).
 * El cliente final del entrenador la abre desde `/p/:short_code` sin auth.
 */
export interface PublicPlanCoach {
  name: string;
  tagline: string | null;
  picture_url: string | null;
  brand_logo_url: string | null;
  brand_color_primary: string | null;
  brand_color_accent: string | null;
  brand_letters: string | null;
  instagram: string | null;
  web: string | null;
  watermark: boolean;
}

export interface PublicPlan {
  short_code: string;
  plan_name: string;
  template: string;
  client_name: string | null;
  /** Lista de PlanSection (mismo shape que el editor — ver types/plan.ts). */
  sections: any[];
  /** Estilo del plan persistido (Feature 3) — el link compartido lo respeta. */
  brand: PlanBrand | null;
  coach: PublicPlanCoach;
  view_count: number;
}

// ---------- CRUD planes ----------

export async function listPlans(): Promise<PlanListItem[]> {
  return apiFetch<PlanListItem[]>("/aetherlite/plans");
}

export async function getPlan(id: number): Promise<Plan> {
  return apiFetch<Plan>(`/aetherlite/plans/${id}`);
}

/** Payload de creación. `name` es el único requerido — el backend defaultea
 *  el resto si no viene. */
export interface CreatePlanPayload {
  name?: string;
  template?: string;
  client_id?: number | null;
  client_name?: string | null;
  sections?: any[];
  brand?: PlanBrand | null;
}

export async function createPlan(plan: CreatePlanPayload): Promise<Plan> {
  return apiFetch<Plan>("/aetherlite/plans", {
    method: "POST",
    body: JSON.stringify(plan),
  });
}

/** Patch parcial — todos los campos son opcionales. Si mandas `client_id`,
 *  el backend valida ownership y sincroniza `client_name` automáticamente
 *  (a menos que también mandes `client_name` explícito). */
export interface UpdatePlanPayload {
  name?: string;
  template?: string;
  client_id?: number | null;
  client_name?: string | null;
  sections?: any[];
  brand?: PlanBrand | null;
}

export async function updatePlan(id: number, patch: UpdatePlanPayload): Promise<Plan> {
  return apiFetch<Plan>(`/aetherlite/plans/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function deletePlan(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/plans/${id}`, { method: "DELETE" });
}

// ---- Share links ----

/**
 * Crea (o devuelve si ya existe) un share link para el plan. El backend es
 * idempotente: si ya hay un share activo, devuelve el mismo `short_code`.
 *
 * El response no incluye `public_url` — la construye el front a partir del
 * short_code y la base de la app pública.
 */
export async function createShare(planId: number, expiresInDays?: number): Promise<PlanShare> {
  return apiFetch<PlanShare>(`/aetherlite/plans/${planId}/share`, {
    method: "POST",
    body: JSON.stringify(expiresInDays ? { expires_in_days: expiresInDays } : {}),
  });
}

export async function revokeShare(planId: number, shortCode: string): Promise<void> {
  return apiFetch<void>(`/aetherlite/plans/${planId}/share/${shortCode}`, {
    method: "DELETE",
  });
}

/**
 * Construye la URL pública (lo que se comparte con el cliente final).
 * En producción es `https://aetherlite.nilabs.co/p/{short_code}`. En dev,
 * usa el origin actual.
 */
export function publicShareUrl(shortCode: string): string {
  if (typeof window === "undefined") return `/p/${shortCode}`;
  return `${window.location.origin}/p/${shortCode}`;
}

/** Lectura pública (sin auth) — es lo que ve el cliente final del entrenador. */
export async function getPublicPlan(shortCode: string): Promise<PublicPlan> {
  return apiFetch<PublicPlan>(`/aetherlite/p/${shortCode}`);
}
